import { useCallback, useEffect, useSyncExternalStore } from 'react';
import type { Extent } from './meshAnnotations';
import type { PartProfile } from './partProfile';
import type { PartItem } from './partsRun';
import type { Job3D } from './threeDApi';

// WHICH MESH THE PART PANEL HAS ON SCREEN, for what stands UNDER it.
//
// The holes table sits below the 3D card and is about the mesh in it: which bores are already
// cut, where each one lands on the box the viewer measured, and — when a row's Drill is
// pressed — which file to cut. All four of those facts live inside ThreeDStep's panel, and the
// table is its sibling in the phase rather than its child, because that is where it reads:
// under the picture, at the foot of the page, not inside the card.
//
// So it is published rather than threaded. The alternative is lifting the shown run, the
// viewer's measurement and its profile three components up to be passed back down — three
// pieces of state moved away from the only place that changes them, to be carried past two
// components with no interest in them. Same arrangement, and the same argument, as
// drillPreview: one panel on screen at a time, so ONE value and no key to get wrong.
//
// NOT PERSISTED. It is what is being looked at this second; a reload has nothing to restore.
export type ShownMesh = {
  job: Job3D;
  // The mesh's extents in the file's own units, as the viewer measured them — null until it
  // has loaded. With the part's printed size it is what millimetres are derived from.
  size: Extent | null;
  // What the manual says the part measures, which anchors those units (see mmScale).
  dims: PartItem['size'];
  // And what shape the mesh is inside its box, so a bore centred on wood that is not there
  // can be placed against the wood instead. See partProfile.
  profile: PartProfile | null;
};

let shown: ShownMesh | null = null;
const listeners = new Set<() => void>();

const announce = (next: ShownMesh | null) => {
  shown = next;
  listeners.forEach((l) => l());
};

export function useShownMesh(): ShownMesh | null {
  const subscribe = useCallback((cb: () => void) => {
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  }, []);
  return useSyncExternalStore(subscribe, () => shown);
}

// PUBLISHED FIELD BY FIELD, and not as one object: a fresh object every render would announce
// on every render, and what is downstream would re-render with it. These four change when the
// run changes, when the viewer finishes measuring, and not otherwise.
//
// Cleared as the panel goes, so a table left standing under a card that has gone is not reading
// a mesh nobody can see.
export function usePublishShownMesh(
  job: Job3D | null,
  size: Extent | null,
  dims: PartItem['size'],
  profile: PartProfile | null,
  // The same run with a new mesh under it, for a drill pressed downstairs. Kept aside rather
  // than published: it is a fresh closure every render and nothing below re-renders when it
  // changes, so announcing it would be an announcement a frame.
  onChanged: (job: Job3D) => void,
): void {
  useEffect(() => { patch = onChanged; });
  useEffect(() => {
    announce(job ? { job, size, dims, profile } : null);
    return () => announce(null);
  }, [job, size, dims, profile]);
}

let patch: ((job: Job3D) => void) | null = null;

// THE MESH CAME BACK CHANGED — a hole cut into it, from the table under the card. Not a new
// row: an edit keeps the run's id, so the panel patches its list rather than refetching one.
export const meshChanged = (job: Job3D): void => patch?.(job);
